import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { Viewerlayer } from "../../../model/viewerlayer";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { UserConfigCommunicatorService } from "../../../services/communicators/user-config-communicator.service";
import { GeoJSONDatabaseService } from "../../../services/internal-services/geojson-database.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BaseViewerComponent } from "../base-viewer/base-viewer.component";
import { IUrlParams } from "../../../interfaces/i-url-params";
import { Observable, of } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { GeoService } from "../../../services/internal-services/geo.service";
import { MapEvents } from "@vision/core-map-api/src/constants/CoreMapConstants";
import { FeatureFlags } from "../../../model/feature-flags";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-compound-viewer",
  templateUrl: "./compound-viewer.component.html",
  styleUrls: ["./compound-viewer.component.scss"],
  standalone: false,
})
export class CompoundViewerComponent extends BaseViewerComponent implements OnDestroy, AfterViewInit {
  // Use Angular inject() for all service injections

  geoJSONDatabaseService = inject(GeoJSONDatabaseService);

  userConfigurationService = inject(UserConfigCommunicatorService);

  userConfigTimeOut: any;

  // region LifeCycle

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.userConfigurationService.userConfigResponse$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.userConfigTimeOut) {
        clearTimeout(this.userConfigTimeOut);
      }
      this.userConfigTimeOut = setTimeout(() => {
        const layers = this.getLayersToSaveConfiguration();
        this.viewerService.saveUserConfiguration(layers);
      }, 60000);
    });
  }

  openImageGalleryDialog(): void {
    console.log("not Implemented in this visor!");
  }

  openImageGalleryReload(): void {
    console.log("not Implemented in this visor!");
  }

  calculateInitLocation(config: any, urlLocation: IUrlParams): Observable<IUrlParams> {
    const location = { ...urlLocation };
    if (urlLocation.address) {
      // get location data from address

      return GeoService.geocoding(urlLocation.address).pipe(
        map((result: any[]) => {
          const lat = result[0].geometry.location.lat();
          const long = result[0].geometry.location.lng();
          location.latitude = lat;
          location.longitude = long;
          if (!location.zoom && config.lastZoom) {
            location.zoom = config.lastZoom;
          }
          return location;
        }),
        first(),
        catchError((error) => {
          console.error("Error on calculateInitLocation geocoding " + error);
          return of(this.processUrlLocation(location, urlLocation, config));
        }),
      );
    }

    this.processUrlLocation(location, urlLocation, config);

    return of(location);
  }

  finishInitialization(): void {
    this.mapService.populateMapStatus();
    const measure3DEnabled = this.featureFlagsService.hasFlag(FeatureFlags.MEASURES_3D);
    if (!measure3DEnabled) {
      this.map.obliqueMeasurements = false; //force disable 3d measurements
    }
    setTimeout(() => {
      this.viewerService.showLoadingComponent();
      this.viewerConfigurationService.initializedMap(true);
    }, 500);
  }

  /**
   *
   * @param config
   */
  preprocessLayers(config: any): Viewerlayer[] {
    {
      if (!config) {
        return [];
      }

      const layers = config.layers;

      if (!layers || layers.length === 0) {
        return layers || [];
      }

      // sync layer config from state url
      this.updateLayerConfig(config, layers);

      // split collection layers in single xyz and discrete layers and add crop filter
      let processedLayers = layers;

      processedLayers
        .filter((layer) => layer.config)
        .map((layer) => {
          layer.enableCropFilter = !!(
            layer.config.filters && layer.config.filters.includes(ViewerConstants.LAYER_FILTERS.CROP)
          );
          return layer;
        });

      return processedLayers;
    }
  }

  /**
   * Handles the first load event by processing the URL parameters.
   * Fetches parameters from the URL, merges them into the state, and triggers appropriate actions
   * based on the presence of address or latitude and longitude in the parameters.
   *
   * @return {void} This method does not return a value.
   */
  handleFirstLoadEvent() {
    const urlParameters = this.urlStateService.getParamsFromURL();
    this.urlStateService.mergeUrlParams(urlParameters);
    if (urlParameters.address) {
      this.handleSearchAddressInUrl(urlParameters.address, this.isMasterMap());
    } else if (urlParameters.latitude && urlParameters.longitude) {
      this.handleUrlParameters(urlParameters);
    }
  }

  /**
   * Configure map options and features
   * check in phoenix viewer for features
   */
  configureMapOptions(config: any) {
    this.viewerConfigurationService.session = config.session || null;

    this.viewerService.allowProperties();
    this.viewerService.allowPropertiesReport();
    this.viewerService.allowExportGeoTiff();

    this.enableContextMenu();
  }

  configureDualView(): void {}

  customOptions(): any {
    return {
      minZoom: 1,
      ignoredEvents: [MapEvents.POINTER_MOVE],
    };
  }

  loadLayers(layers: any[]): void {
    this.loadCommonLayers(layers);
  }
}
